import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Only call /auth/me if a JWT token exists (prevents 401 console spam for anonymous users)
    const token = localStorage.getItem("jh_token");
    if (!token) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithCreds = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("jh_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("jh_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Phone OTP — Jahez-style
  const sendPhoneOtp = async (phone) => {
    const { data } = await api.post("/auth/phone/send-otp", { phone });
    return data; // { ok, phone, expires_in, _dev_code (mock only) }
  };

  const verifyPhoneOtp = async (phone, code, name) => {
    const { data } = await api.post("/auth/phone/verify-otp", { phone, code, name });
    if (data.need_name) return { needName: true, phone: data.phone };
    localStorage.setItem("jh_token", data.token);
    setUser(data.user);
    return { user: data.user, isNew: data.is_new_user };
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("jh_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, loginWithCreds, register, sendPhoneOtp, verifyPhoneOtp, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
